import React, { useEffect, useState, useMemo, useRef, useContext, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getQuestions,
  getSubjects,
  getPurposes,
  getObjectives,
  getTopics,
  getSubtopics,
  getStatuses,
  getStatusCount,
  getQuestionFolders,
} from "../../services/questionService";
import { getTests } from "../../services/Exams/ExamsService";
import Table, { isDraftQuestion } from "./Table";
import { setFilterState, getFilterState } from "../../utils/filterState";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import Select from "../../components/forms/Select/Select";
import { filterSelectStyles } from "../../components/forms/Select/styles";
import AsyncSearchSelect from "../../components/forms/Select/AsyncSearchSelect";
import { UserContext } from "../../utils/contexts/UserContext";
import { toast } from "react-toastify";
import { deleteQuestion } from "../../services/MyQuestions/QuestionService";
import { isCanceledError } from "../../utils/examError";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { bookletLockMessage, lockingBooklets } from "../QuestionPage/review/bookletLock";
import { useTranslation } from "react-i18next";
import FolderBar from "../../components/QuestionFolders/FolderBar";
import SelectionBar from "../../components/QuestionFolders/SelectionBar";
import FolderNameDialog, { examSavedMessage } from "../../components/QuestionFolders/FolderNameDialog";
import DeleteFolderDialog from "../../components/QuestionFolders/DeleteFolderDialog";
import MoveToFolderDialog from "../../components/QuestionFolders/MoveToFolderDialog";
import UploadQuestionsDialog from "../../components/QuestionFolders/UploadQuestionsDialog";
import AddSubjectDialog from "../../components/QuestionFolders/AddSubjectDialog";
import ShareFolderDialog from "../../components/QuestionFolders/ShareFolderDialog";
import FolderMatrix from "../../components/QuestionFolders/FolderMatrix";
import FolderTasks from "../../components/QuestionFolders/tasks/FolderTasks";
import SubjectSettingsDialog from "../../components/QuestionFolders/SubjectSettingsDialog";
import SubjectVariants from "../../components/SubjectVariants/SubjectVariants";
import { canBuildVariants, canSeeTasksIn, folderPath, folderTree, isAdminUser, isSubjectFolder } from "../../utils/folders";
import BankTabs, { usePassagesAvailable } from "../../components/Passages/BankTabs";
import { DIFFICULTY_LEVELS, difficultyLabel } from "../../components/Difficulty/Difficulty";
import StatusFilterBar from "../../components/QuestionList/StatusFilterBar";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import QuestionCards from "../../components/QuestionList/QuestionCards";
import { HelpTitle } from "../../components/Help/HelpButton";
import BankGuide from "../../components/Help/guides/BankGuide";
import { useMediaQuery } from "../Exams/TestBuilder/ui";
import { formatDate } from "../../utils/formatDate";

const PAGE_SIZE = 50;

const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);
const filterValue = (value) => (value !== "all" ? value : undefined);
const optionLabel = (options, value) => options.find((o) => o.value === value)?.label || "";

// "First Last", or the username when both are empty (accounts that uploaded docx files have no name).
const authorName = (author) =>
  [author?.first_name, author?.last_name].filter(Boolean).join(" ").trim() || author?.username || "";

export default function Questions() {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  // A subject variant's "find questions in the bank" link presets the topic, the subtopic and the difficulty
  // (?topic=&topic_name=&subtopic=&subtopic_name=&difficulty=) instead of the saved filters; the rest stay at all.
  // The saved filters bring their topic back only to the folder it was picked in (topicFolder): topics belong to a
  // subject, and a link to another folder (a BTS variant's, a folder link, the Texts tab's way back from another
  // folder) would list nothing with it.
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const linkTopic = params.get("topic");
    const linkLevel = DIFFICULTY_LEVELS.includes(params.get("difficulty")) ? params.get("difficulty") : null;
    if (!linkTopic && !linkLevel) {
      const saved = getFilterState();
      const opened = params.get("folder") || saved.folder || "all";
      return opened === (saved.topicFolder || "all")
        ? saved
        : { ...saved, topic: undefined, topicOption: null, subtopic: undefined, subtopicOption: null };
    }
    const topicOption = linkTopic ? { value: linkTopic, label: params.get("topic_name") || "" } : null;
    const subtopicOption =
      linkTopic && params.get("subtopic") ? { value: params.get("subtopic"), label: params.get("subtopic_name") || "" } : null;
    return {
      fromLink: true,
      topic: topicOption?.value,
      topicOption,
      subtopic: subtopicOption?.value,
      subtopicOption,
      difficulty: linkLevel || undefined,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const abortControllerRef = useRef(null);
  const countsControllerRef = useRef(null);
  // Page of the latest list request: the page effect skips the page 1 that a filter reset already fetched.
  const fetchedPageRef = useRef(null);
  // Phones get the list as cards instead of the wide table.
  const wide = useMediaQuery("(min-width: 768px)");

  const { user } = useContext(UserContext);

  const [questions, setQuestions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const [search, setSearch] = useState(initialFilters.search || "");
  const [grade, setGrade] = useState(initialFilters.grade || "all");
  const [subject, setSubject] = useState(initialFilters.subject || "all");
  const [purpose, setPurpose] = useState(initialFilters.purpose || "all");
  const [objective, setObjective] = useState(initialFilters.objective || "all");
  // A topic comes back only with its option (its name for the select); My Questions keeps just the uuid.
  const [topic, setTopic] = useState((initialFilters.topicOption && initialFilters.topic) || "all");
  const [subtopic, setSubtopic] = useState((initialFilters.topicOption && initialFilters.subtopicOption && initialFilters.subtopic) || "all");
  const [status, setStatus] = useState(initialFilters.status || "all");
  // "all", "null" (No folder), an exam's or a subject's uuid. In the address (?folder=), so a reload or a
  // folder link (FolderLink) opens it; ?tab=matrix opens a subject's matrix.
  const [searchParams, setSearchParams] = useSearchParams();
  const folder = searchParams.get("folder") || "all";
  // react-router gives a new setSearchParams on every change of the address: setFolder calls the latest one, so it
  // stays one function and the folder list is not loaded again whenever ?tab= or ?variant= changes.
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  // The loaded folders by uuid (set below), for setFolder.
  const byUuidRef = useRef(null);
  const setFolder = useCallback(
    (next) =>
      setParamsRef.current(
        (prev) => {
          const params = new URLSearchParams(prev);
          const value = typeof next === "function" ? next(params.get("folder") || "all") : next;
          if (!value || value === "all") params.delete("folder");
          else params.set("folder", value);
          // Another folder closes the previous subject's variant and opens on its questions, but the Subject variant
          // tab stays open from subject to subject where the role builds variants.
          if ((value || "all") !== (prev.get("folder") || "all")) {
            const target = byUuidRef.current?.get(value);
            const keepTab = params.get("tab") === "variant" && isSubjectFolder(target) && canBuildVariants(target.role);
            if (!keepTab) params.delete("tab");
            // A review task's questions are of its subject.
            params.delete("task");
            params.delete("task_left");
            // The variant list's filters (grade, status, page) were the previous subject's.
            ["variant", "vgrade", "vstatus", "vpage"].forEach((key) => params.delete(key));
          }
          return params;
        },
        { replace: true }
      ),
    []
  );
  const folderRef = useRef(folder);
  folderRef.current = folder;
  const [test, setTest] = useState(initialFilters.test || "all");
  // "all", "null" (no difficulty) or A / B / C
  const [difficulty, setDifficulty] = useState(initialFilters.difficulty || "all");
  const [ordering, setOrdering] = useState(initialFilters.ordering || "created");

  const [subjectOptions, setSubjectOptions] = useState([]);
  const [purposeOptions, setPurposeOptions] = useState([]);
  const [objectiveOptions, setObjectiveOptions] = useState([]);
  // The chosen topic and subtopic ({value, label}); restored with the filters when coming back to the bank.
  const [topicSelectValue, setTopicSelectValue] = useState(initialFilters.topic ? initialFilters.topicOption || null : null);
  const [subtopicSelectValue, setSubtopicSelectValue] = useState(
    initialFilters.topicOption && initialFilters.subtopic ? initialFilters.subtopicOption || null : null
  );
  const [statusOptions, setStatusOptions] = useState([]);
  const [allStatusCounts, setAllStatusCounts] = useState([]);
  const [testOptions, setTestOptions] = useState([]);

  // Folders: hidden when the backend has no folder API (old deployment).
  const [folders, setFolders] = useState([]);
  const [foldersAvailable, setFoldersAvailable] = useState(false);
  const [noFolderCount, setNoFolderCount] = useState(null);
  const [folderDialog, setFolderDialog] = useState({ open: false, folder: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, folder: null });
  const [subjectDialog, setSubjectDialog] = useState({ open: false, exam: null });
  const [subjectSettings, setSubjectSettings] = useState({ open: false, folder: null });
  // "Share" of a subject (its moderator and admins); the folder stays set while the dialog closes.
  const [shareDialog, setShareDialog] = useState({ open: false, folder: null });

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // The codes of the questions ticked (uuid -> code), kept from every page: the move dialog names by them those an
  // active BTS booklet keeps in their folder.
  const selectedCodesRef = useRef(new Map());
  const [moveOpen, setMoveOpen] = useState(false);
  // The list's top: paging from under the list scrolls back to it.
  const listRef = useRef(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // The Texts tab: hidden when the backend has no passages API (old deployment).
  const passagesAvailable = usePassagesAvailable();

  const { exams, byUuid } = useMemo(() => folderTree(folders), [folders]);
  byUuidRef.current = byUuid;
  const currentFolder = byUuid.get(folder) || null;
  const currentSubject = isSubjectFolder(currentFolder) ? currentFolder : null;
  // Topics belong to a catalog subject: the open subject folder's, else the subject filter's.
  const topicSubject = currentSubject?.subject?.uuid || (subject !== "all" && subject !== "null" ? subject : undefined);
  // A subject shows its questions, its matrix (?tab=matrix) or, for its moderator and admins, its variants
  // (?tab=variant, the open one in ?variant=). A matrix or variant link waits for the folder list (the subject and
  // the user's role in it) on a loader ("pending"), not on the question list.
  const tab = searchParams.get("tab");
  const view =
    !foldersAvailable && (tab === "matrix" || tab === "variant" || tab === "tasks")
      ? "pending"
      : !currentSubject
        ? "questions"
        : tab === "matrix" ||
            (tab === "variant" && canBuildVariants(currentSubject.role)) ||
            (tab === "tasks" && canSeeTasksIn(currentSubject.role))
          ? tab
          : "questions";
  // A review task (?task=<uuid>, from its "Open the questions"; &task_left=true: only those its assignee has not voted
  // on yet): the list shows its questions. The task's card names it in the link's state.
  const reviewTask = searchParams.get("task") || "";
  const taskLeft = !!reviewTask && searchParams.get("task_left") === "true";
  const taskLabel = location.state?.taskLabel;

  const canVote = useMemo(() => {
    return user?.perms?.includes('vote_question') || false;
  }, [user]);
  const perms = user?.perms || [];
  // Exams and their subjects are managed by admins only.
  const isAdmin = isAdminUser(user);
  const folderRoles = user?.folder_roles || [];
  const hasGlobalWrite = perms.includes("create_question") || perms.includes("create_exam");
  // Moving into a subject: its moderator, or an author with their own questions (the server checks each).
  const canMove = hasGlobalWrite || isAdmin || folderRoles.includes("moderator") || folderRoles.includes("author");
  const selectable = canMove && foldersAvailable;

  const gradeOptions = useMemo(() => [
    { value: "all", label: t("questions_page.filters.all_grades") },
    { value: -1, label: t("questions_page.filters.teachers") },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: t("questions_page.filters.grade_n", { grade: i + 1 })
    }))
  ], [t]);

  const selectSubjectOptions = useMemo(() => [
    { value: "all", label: t("questions_page.filters.all_subjects") },
    ...subjectOptions.map(s => ({ value: s.uuid, label: s.name })),
    ...(foldersAvailable ? [{ value: "null", label: t("folders.no_subject") }] : []),
  ], [subjectOptions, foldersAvailable, t]);

  const selectPurposeOptions = useMemo(() => [
    { value: "all", label: t("questions_page.filters.all_terms") },
    ...purposeOptions.map(p => ({ value: p.uuid, label: p.name }))
  ], [purposeOptions, t]);

  const selectObjectiveOptions = useMemo(() => [
    { value: "all", label: t("questions_page.filters.all_objectives") },
    ...objectiveOptions.map(o => ({ value: o.uuid, label: o.name }))
  ], [objectiveOptions, t]);

  const selectDifficultyOptions = useMemo(() => [
    { value: "all", label: t("difficulty.filter_all") },
    ...DIFFICULTY_LEVELS.map((level) => ({ value: level, label: difficultyLabel(t, level) })),
    { value: "null", label: t("difficulty.filter_none") },
  ], [t]);

  const selectTestOptions = useMemo(() => [
    { value: "all", label: t("questions_page.filters.all_tests") },
    ...testOptions.map((test) => ({ value: test.uuid, label: test.name })),
  ], [testOptions, t]);

  // Every list filter; the counters reuse them without their own dimension.
  const filterParams = useMemo(() => ({
    subject: filterValue(subject),
    grade: filterValue(grade),
    purpose: filterValue(purpose),
    objective: filterValue(objective),
    topic: filterValue(topic),
    subtopic: filterValue(subtopic),
    status: filterValue(status),
    search: search || undefined,
    folder: filterValue(folder),
    test: hasGlobalWrite ? filterValue(test) : undefined,
    difficulty: filterValue(difficulty),
    task: reviewTask || undefined,
    task_left: taskLeft ? "true" : undefined,
  }), [subject, grade, purpose, objective, topic, subtopic, status, search, folder, test, hasGlobalWrite, difficulty, reviewTask, taskLeft]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        const [subjects, purposes, objectives, statuses] = await Promise.all([
          getSubjects({ params: { page: 1, page_size: 100 }, signal: controller.signal }),
          getPurposes({ params: { page: 1, page_size: 100 }, signal: controller.signal }),
          getObjectives({ params: { page: 1, page_size: 100 }, signal: controller.signal }),
          getStatuses({ params: { page: 1, page_size: 100 }, signal: controller.signal }),
        ]);

        setSubjectOptions(listOf(subjects));
        setPurposeOptions(listOf(purposes));
        setObjectiveOptions(listOf(objectives));
        setStatusOptions(listOf(statuses));
        setInitialDataLoaded(true);
      } catch (err) {
        if (!isCanceledError(err)) {
          console.error("Failed to fetch initial data:", err);
        }
      }
    };

    fetchInitialData();

    return () => {
      controller.abort();
    };
  }, []);

  // Folder list + the "No folder" count. If the first load fails (old backend), the folder UI stays hidden; a later
  // load that fails keeps the folders and the open folder.
  const refreshesRef = useRef(0);
  const loadFolders = useCallback(async ({ signal, initial = false } = {}) => {
    // A refresh (no signal: nothing cancels it) is counted while under way: it checks the open folder when it lands.
    if (!signal) refreshesRef.current += 1;
    try {
      const [list, noFolder] = await Promise.all([
        getQuestionFolders({ signal }),
        getQuestions({ params: { page: 1, page_size: 1, folder: "null" }, signal }).catch((err) => {
          if (isCanceledError(err)) throw err;
          return null;
        }),
      ]);
      if (!Array.isArray(list)) throw new Error("Unexpected question_folders response");
      setFolders(list);
      setNoFolderCount(typeof noFolder?.count === "number" ? noFolder.count : null);
      setFoldersAvailable(true);
      // A folder the user can not see (a link to another subject, a deleted folder) falls back to all.
      const current = folderRef.current;
      if (current !== "all" && current !== "null" && !list.some((f) => f.uuid === current)) {
        toast.error(t("folders.no_access"));
        setFolder("all");
      }
    } catch (err) {
      if (!isCanceledError(err) && initial) {
        setFoldersAvailable(false);
        setFolders([]);
        setFolder("all");
      }
    } finally {
      if (!signal) refreshesRef.current -= 1;
    }
  }, [setFolder, t]);
  const loadFoldersRef = useRef(loadFolders);
  loadFoldersRef.current = loadFolders;

  // Back to the bank from another page: the folder it had, unless the address names one.
  useEffect(() => {
    if (!searchParams.get("folder") && initialFilters.folder && initialFilters.folder !== "all") setFolder(initialFilters.folder);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A link's filters leave the address once they are read: they now live in the filters.
  useEffect(() => {
    if (!initialFilters.fromLink) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        ["topic", "topic_name", "subtopic", "subtopic_name", "difficulty"].forEach((key) => params.delete(key));
        return params;
      },
      { replace: true }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The folder list loads once on opening the page; what changes folders loads it again (loadFolders()).
  useEffect(() => {
    const controller = new AbortController();
    loadFoldersRef.current({ signal: controller.signal, initial: true });
    return () => controller.abort();
  }, []);

  // A folder the address names later (Back / Forward, a folder link) that the loaded list lacks: a fresh list says
  // whether it is new or out of reach (then all questions). A refresh under way checks it when it lands.
  useEffect(() => {
    if (!foldersAvailable || folder === "all" || folder === "null" || byUuid.has(folder) || refreshesRef.current) {
      return undefined;
    }
    const controller = new AbortController();
    loadFoldersRef.current({ signal: controller.signal });
    return () => controller.abort();
  }, [folder, foldersAvailable, byUuid]);

  // Tests for the "questions of a test" filter (only for users with global rights).
  useEffect(() => {
    if (!hasGlobalWrite) return undefined;
    const controller = new AbortController();
    getTests({ params: { page: 1, page_size: 500 }, signal: controller.signal })
      .then((res) => setTestOptions(listOf(res)))
      .catch(() => setTestOptions([]));
    return () => controller.abort();
  }, [hasGlobalWrite]);

  const fetchTopicOptions = useCallback((search) => {
    return getTopics({
      params: {
        page: 1,
        page_size: 100,
        subject: topicSubject,
        grade: grade !== "all" ? grade : undefined,
        search: search || undefined,
      },
    }).then((res) => {
      const arr = Array.isArray(res) ? res : res?.results || [];
      return arr;
    });
  }, [topicSubject, grade]);

  // Another subject's folder: the topic picked in the previous subject does not apply there.
  const topicSubjectRef = useRef(topicSubject);
  useEffect(() => {
    const previous = topicSubjectRef.current;
    topicSubjectRef.current = topicSubject;
    if (previous && topicSubject && previous !== topicSubject) {
      setTopic("all");
      setSubtopic("all");
      setTopicSelectValue(null);
      setSubtopicSelectValue(null);
    }
  }, [topicSubject]);

  // The filters are shared with My Questions, which may keep its Draft status; the bank lists no drafts.
  useEffect(() => {
    const selected = statusOptions.find((o) => o.uuid === status);
    if (selected && (selected.code === "DRAFT" || (!selected.code && selected.name === "Draft"))) setStatus("all");
  }, [statusOptions, status]);

  const fetchSubtopicOptions = useCallback((search) => {
    if (topic === "all") return Promise.resolve([]);
    return getSubtopics({
      params: {
        page: 1,
        page_size: 100,
        topic,
        search: search || undefined,
      },
    }).then((res) => {
      const arr = Array.isArray(res) ? res : res?.results || [];
      return arr;
    });
  }, [topic]);

  // When filters change, reset to page 1 and fetch, while the question list shows: a subject's matrix and variants
  // need neither the list nor its counts. Back on the list, the same filters keep its page and selection.
  const listKeyRef = useRef(null);
  const listShown = view === "questions";
  useEffect(() => {
    if (!initialDataLoaded || !listShown) return;
    const key = JSON.stringify([filterParams, ordering]);
    if (listKeyRef.current?.key === key && listKeyRef.current.user === user) return;
    listKeyRef.current = { key, user };
    setPage(1);
    setSelectedIds((prev) => (prev.size ? new Set() : prev));
    fetchQuestions(1);
    fetchCounts();
  }, [initialDataLoaded, user, filterParams, ordering, listShown]);

  // When page changes (but not from filter reset), fetch that page, page 1 included
  useEffect(() => {
    if (initialDataLoaded && page !== fetchedPageRef.current) {
      fetchQuestions(page);
    }
  }, [page]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const fetchQuestions = async (currentPage) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchedPageRef.current = currentPage;
    setLoading(true);

    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        ordering,
        ...filterParams,
      };

      if (canVote && user?.uuid) {
        params.evaluator = user.uuid;
      }

      const questionsData = await getQuestions({
        params,
        signal: controller.signal,
      });

      const results = questionsData?.results || [];
      setQuestions(results);
      setTotalCount(questionsData?.count || 0);

    } catch (err) {
      // A page that no longer exists (fewer results than before): back to the first one.
      if (err?.response?.status === 404 && currentPage > 1) {
        setPage(1);
        return;
      }
      if (!isCanceledError(err)) {
        console.error("Failed to fetch questions:", err);
        setQuestions([]);
      }
    } finally {
      // An aborted request must not hide the spinner of the request that replaced it.
      if (abortControllerRef.current === controller) setLoading(false);
    }
  };

  // Status counts with the current filters (but any status).
  const fetchCounts = () => {
    if (countsControllerRef.current) {
      countsControllerRef.current.abort();
    }
    const controller = new AbortController();
    countsControllerRef.current = controller;
    const { status: _status, ...withoutStatus } = filterParams;

    getStatusCount({ params: { page: 1, page_size: 100, ...withoutStatus }, signal: controller.signal })
      .then((res) => setAllStatusCounts(Array.isArray(res) ? res : []))
      .catch((err) => {
        if (!isCanceledError(err)) console.error("Failed to fetch status counts:", err);
      });
  };

  // `topicFolder` stays the folder the topic belongs to when the Texts tab saves its own folder.
  const handleStoreFilters = () => {
    setFilterState({
      search, grade, subject, purpose, objective, topic, subtopic, status, folder, test, difficulty, ordering,
      topicOption: topicSelectValue,
      subtopicOption: subtopicSelectValue,
      topicFolder: folder,
    });
  };

  // A passage chip opens its passage on the bank's Texts tab.
  const openPassage = (passage) => {
    handleStoreFilters();
    navigate(`/questions/texts?open=${passage.uuid}`);
  };

  // Grade and subject narrow the topics, so they clear the chosen topic and subtopic.
  const clearTopic = () => {
    setTopic("all");
    setSubtopic("all");
    setTopicSelectValue(null);
    setSubtopicSelectValue(null);
  };
  const changeGrade = (value) => {
    setGrade(value);
    clearTopic();
  };
  const changeSubject = (value) => {
    setSubject(value);
    clearTopic();
  };

  // The review task's filter off, or only its "not voted yet" (`keepTask`).
  const clearTask = (keepTask = false) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (!keepTask) params.delete("task");
        params.delete("task_left");
        return params;
      },
      { replace: true }
    );

  // Every filter back to "all"; the sort order stays.
  const handleReset = () => {
    if (reviewTask) clearTask();
    setSearch("");
    setGrade("all");
    setSubject("all");
    setPurpose("all");
    setObjective("all");
    clearTopic();
    setStatus("all");
    setTest("all");
    setDifficulty("all");
  };

  const handleDelete = (uuid) => {
    toast(
      ({ closeToast }) => (
        <div>
          <p className="mb-3 text-sm font-medium text-slate-900">{t("questions_page.delete_confirm")}</p>
          <div className="flex gap-2">
            <button
              className="inline-flex h-8 items-center justify-center rounded-lg bg-red-600 px-3 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              onClick={() => {
                deleteQuestion(uuid)
                  .then(() => {
                    toast.success(t("questions_page.toast.deleted"));
                    fetchQuestions(page);
                  })
                  .catch((err) => {
                    // A question an active BTS booklet prints says why it cannot be deleted, and who can unlock it.
                    toast.error(
                      getApiErrorCode(err) === "question_in_booklet"
                        ? bookletLockMessage(t, "question_detail.review.delete_in_booklet", lockingBooklets(err), false)
                        : getApiErrorMessage(t, err, "questions_page.toast.delete_failed")
                    );
                  });
                closeToast();
              }}
            >
              {t("buttons.delete")}
            </button>
            <button
              className="inline-flex h-8 items-center justify-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              onClick={closeToast}
            >
              {t("buttons.cancel")}
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false }
    );
  };

  const handleSort = (field) => {
    if (ordering === field) {
      setOrdering(`-${field}`);
    } else if (ordering === `-${field}`) {
      setOrdering("created");
    } else {
      setOrdering(field);
    }
  };

  const toggleSelect = useCallback((question) => {
    selectedCodesRef.current.set(question.uuid, question.code || "");
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(question.uuid)) next.delete(question.uuid);
      else next.add(question.uuid);
      return next;
    });
  }, []);

  const togglePage = useCallback((pageQuestions, checked) => {
    pageQuestions.forEach((q) => selectedCodesRef.current.set(q.uuid, q.code || ""));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageQuestions.forEach((q) => (checked ? next.add(q.uuid) : next.delete(q.uuid)));
      return next;
    });
  }, []);

  // The move dialog takes out of the selection the questions an active BTS booklet keeps, before moving the others.
  const deselect = useCallback((uuids) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      uuids.forEach((uuid) => next.delete(uuid));
      return next;
    });
  }, []);

  // The pager under the list brings its top back into view.
  const pageFromBottom = (target) => {
    setPage(target);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    listRef.current?.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
  };

  const refreshAfterChange = () => {
    loadFolders();
    // Off the question list (an upload from a subject's matrix or variants), the list loads when it shows again.
    if (!listShown) {
      listKeyRef.current = null;
      return;
    }
    setPage(1);
    fetchQuestions(1);
    fetchCounts();
  };

  const handleMoved = (result) => {
    setMoveOpen(false);
    setSelectedIds(new Set());
    toast.success(t("folders.move.moved", { count: result?.moved ?? selectedIds.size }));
    refreshAfterChange();
  };

  const examOf = (f) => (f?.parent ? byUuid.get(f.parent) : f) || null;
  const setView = (next) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "matrix" || next === "variant" || next === "tasks") params.set("tab", next);
        else params.delete("tab");
        if (next !== "variant") params.delete("variant");
        return params;
      },
      { replace: true }
    );

  // An exam created (and opened) or its settings saved (name, only approved questions in tests).
  const handleFolderSaved = (saved) => {
    const before = folderDialog.folder;
    setFolderDialog({ open: false, folder: before });
    toast.success(examSavedMessage(t, before, saved));
    if (!before && saved?.uuid) setFolder(saved.uuid);
    loadFolders();
  };

  const handleSubjectAdded = (saved) => {
    setSubjectDialog((d) => ({ ...d, open: false }));
    toast.success(t("folders.toast.subject_added"));
    if (saved?.uuid) setFolder(saved.uuid);
    loadFolders();
  };
  const handleSubjectSaved = () => {
    setSubjectSettings((d) => ({ ...d, open: false }));
    toast.success(t("folders.toast.subject_saved"));
    loadFolders();
  };

  // A deleted subject leaves its exam open; a deleted exam, all questions.
  const handleFolderDeleted = (deleted) => {
    setDeleteDialog({ open: false, folder: deleted });
    toast.success(deleted.parent ? t("folders.toast.subject_deleted") : t("folders.toast.exam_deleted"));
    if (folder === deleted.uuid) setFolder(deleted.parent || "all");
    else if (!deleted.parent && byUuid.get(folder)?.parent === deleted.uuid) setFolder("all");
    loadFolders();
  };

  // The test filter is part of the new API, so it follows the folder API availability.
  const showTestFilter = selectable && (testOptions.length > 0 || test !== "all");
  const testActive = hasGlobalWrite && test !== "all";

  // Main filters on screen; the rarer ones behind "More filters". Inside a subject the subject filter is
  // the folder itself, so it is not offered there.
  const mainFilters = [
    !currentSubject && {
      key: "subject",
      active: subject !== "all",
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.subject")}
          options={selectSubjectOptions}
          value={subject}
          onChange={(option) => changeSubject(option?.value || "all")}
          placeholder={t("questions_page.filters.all_subjects")}
          isSearchable={false}
        />
      ),
    },
    {
      key: "topic",
      active: topic !== "all",
      // Remounted on subject/grade change: react-select/async loads its default options only on mount.
      node: (
        <AsyncSearchSelect
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.topic")}
          key={`${topicSubject || "all"}|${grade}`}
          placeholder={t("questions_page.filters.all_topics")}
          value={topicSelectValue}
          isClearable
          fetchOptions={fetchTopicOptions}
          onChange={(v) => {
            setTopicSelectValue(v);
            setTopic(v ? v.value : "all");
            setSubtopic("all");
            setSubtopicSelectValue(null);
          }}
        />
      ),
    },
    // The subtopic shows once a topic is chosen: it lists that topic's subtopics only.
    topic !== "all" && {
      key: "subtopic",
      active: subtopic !== "all",
      node: (
        <AsyncSearchSelect
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.subtopic")}
          key={topic}
          placeholder={t("questions_page.filters.all_subtopics")}
          value={subtopicSelectValue}
          isClearable
          fetchOptions={fetchSubtopicOptions}
          onChange={(v) => {
            setSubtopicSelectValue(v);
            setSubtopic(v ? v.value : "all");
          }}
        />
      ),
    },
    {
      key: "difficulty",
      active: difficulty !== "all",
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t("difficulty.label")}
          options={selectDifficultyOptions}
          value={difficulty}
          onChange={(option) => setDifficulty(option?.value || "all")}
          placeholder={t("difficulty.filter_all")}
          isSearchable={false}
        />
      ),
    },
  ].filter(Boolean);

  const moreFilters = [
    {
      key: "grade",
      active: grade !== "all",
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.grade")}
          options={gradeOptions}
          value={grade}
          onChange={(option) => changeGrade(option?.value || "all")}
          placeholder={t("questions_page.filters.all_grades")}
          isSearchable={false}
        />
      ),
    },
    {
      key: "term",
      active: purpose !== "all",
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.term")}
          options={selectPurposeOptions}
          value={purpose}
          onChange={(option) => setPurpose(option?.value || "all")}
          placeholder={t("questions_page.filters.all_terms")}
          isSearchable={false}
        />
      ),
    },
    {
      key: "objective",
      active: objective !== "all",
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.objective")}
          options={selectObjectiveOptions}
          value={objective}
          onChange={(option) => setObjective(option?.value || "all")}
          placeholder={t("questions_page.filters.all_objectives")}
          isSearchable={false}
        />
      ),
    },
    showTestFilter && {
      key: "test",
      active: testActive,
      node: (
        <Select
          customStyles={filterSelectStyles}
          ariaLabel={t("question_list.filter.test")}
          options={selectTestOptions}
          value={test}
          onChange={(option) => setTest(option?.value || "all")}
          placeholder={t("questions_page.filters.all_tests")}
          isSearchable
        />
      ),
    },
  ].filter(Boolean);

  // The active filters that are not on screen: those behind "More filters", and the subject inside a subject.
  const filterChips = [
    currentSubject && subject !== "all" && {
      key: "subject",
      label: t("question_list.filter.subject"),
      value: optionLabel(selectSubjectOptions, subject),
      onRemove: () => changeSubject("all"),
    },
    grade !== "all" && {
      key: "grade",
      label: t("question_list.filter.grade"),
      value: optionLabel(gradeOptions, grade),
      onRemove: () => changeGrade("all"),
    },
    purpose !== "all" && {
      key: "term",
      label: t("question_list.filter.term"),
      value: optionLabel(selectPurposeOptions, purpose),
      onRemove: () => setPurpose("all"),
    },
    objective !== "all" && {
      key: "objective",
      label: t("question_list.filter.objective"),
      value: optionLabel(selectObjectiveOptions, objective),
      onRemove: () => setObjective("all"),
    },
    testActive && {
      key: "test",
      label: t("question_list.filter.test"),
      value: optionLabel(selectTestOptions, test),
      onRemove: () => setTest("all"),
    },
    reviewTask && {
      key: "task",
      label: t("folders.tasks.task_chip"),
      value: taskLabel || t("folders.tasks.kind.review"),
      onRemove: () => clearTask(),
    },
    taskLeft && {
      key: "task_left",
      label: t("folders.tasks.left_chip"),
      value: t("folders.tasks.left_chip_value"),
      onRemove: () => clearTask(true),
    },
  ].filter(Boolean);

  const filtersActive =
    !!search ||
    [grade, subject, purpose, objective, topic, subtopic, status, difficulty].some((value) => value !== "all") ||
    testActive ||
    !!reviewTask;

  const draftCheckbox = (question) =>
    isDraftQuestion(question)
      ? { label: t("errors.drafts_not_allowed"), disabled: true }
      : { label: t("folders.select_question", { code: question.code || "" }) };

  // The guide names FolderBar's "N more" with its count: the folders with no questions but the open one.
  const openExamUuid = currentFolder ? currentFolder.parent || currentFolder.uuid : null;
  const emptyFolders = exams.filter((e) => e.questions_count === 0 && e.uuid !== openExamUuid).length;

  return (
    <div className="min-w-0 space-y-5 motion-safe:animate-fade-in">
      <div className="space-y-4">
        <div>
          {/* The guide's "?" right after the title. */}
          <HelpTitle
            helpId="bank"
            helpTitle={t("help.bank.title")}
            guide={<BankGuide canMove={selectable} emptyFolders={emptyFolders} inSubject={!!currentSubject} />}
          >
            {t("nav.question_bank")}
          </HelpTitle>
          <p className="mt-1 text-sm text-slate-500">{t("questions_page.subtitle")}</p>
        </div>
        <BankTabs
          active="questions"
          showTexts={passagesAvailable}
          onLeave={handleStoreFilters}
          search={folder !== "all" ? `?folder=${folder}` : ""}
        />
      </div>

      {foldersAvailable && (
        <FolderBar
          folders={folders}
          noFolderCount={noFolderCount}
          selected={folder}
          onSelect={setFolder}
          isAdmin={isAdmin}
          onCreateExam={() => setFolderDialog({ open: true, folder: null })}
          onEditExam={(f) => setFolderDialog({ open: true, folder: f })}
          onAddSubject={(exam) => setSubjectDialog({ open: true, exam })}
          onEditSubject={(subject) => setSubjectSettings({ open: true, folder: subject })}
          onDeleteFolder={(f) => setDeleteDialog({ open: true, folder: f })}
          onUpload={() => setUploadOpen(true)}
          onShare={(subject) => setShareDialog({ open: true, folder: subject })}
          view={view}
          onViewChange={setView}
          showVariants
        />
      )}

      {view === "pending" ? (
        <div role="status" className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true"></div>
            <p className="text-sm text-slate-500">{t("folders.loading_subject")}</p>
          </div>
        </div>
      ) : view === "matrix" ? (
        <FolderMatrix folder={currentSubject} folders={folders} />
      ) : view === "tasks" ? (
        <FolderTasks key={currentSubject.uuid} folder={currentSubject} />
      ) : view === "variant" ? (
        // Each subject's variants start afresh (their grade, status and page), as the tab stays from subject to subject.
        <SubjectVariants key={currentSubject.uuid} folder={currentSubject} />
      ) : (
      <>
      <StatusFilterBar counts={allStatusCounts} statusOptions={statusOptions} selected={status} onSelect={setStatus} />

      <FilterPanel
        search={search}
        onSearchChange={setSearch}
        filters={mainFilters}
        more={moreFilters}
        chips={filterChips}
        canClear={filtersActive}
        onClearAll={handleReset}
      />

      {/* A review task's questions: what counts one for the task. */}
      {reviewTask && (
        <p className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900 ring-1 ring-inset ring-blue-600/15">
          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          {t("folders.tasks.bank_hint")}
        </p>
      )}

      {/* Ticked questions turn the count line into their bar, in the same place: nothing moves under the pointer.
          The pages are under the list only; the bar still says which one is on screen. */}
      {selectable && selectedIds.size > 0 ? (
        <SelectionBar
          count={selectedIds.size}
          onMove={() => setMoveOpen(true)}
          onClear={() => setSelectedIds(new Set())}
          page={page}
          pageCount={totalPages}
        />
      ) : (
        <ResultsBar
          count={totalCount}
          page={page}
          pageCount={totalPages}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onPage={setPage}
          loading={loading}
          pager={false}
        />
      )}

      <div ref={listRef} className="min-w-0 scroll-mt-16 overflow-hidden">
      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <p className="text-sm text-slate-500">{t("questions_page.loading")}</p>
          </div>
        </div>
      ) : questions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
          <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
            <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-medium text-slate-900">{t("questions_page.empty.title")}</h3>
          {/* "Clear all" is the filters card's, right above: not repeated here. Without filters the place is empty. */}
          <p className="mt-1 text-sm text-slate-500">
            {filtersActive ? t("questions_page.empty.description") : t("questions_page.empty.none_here")}
          </p>
        </div>
      ) : wide ? (
        <Table
          questions={questions}
          onStoreFilters={handleStoreFilters}
          canVote={canVote}
          ordering={ordering}
          onSort={handleSort}
          loading={loading}
          onDelete={handleDelete}
          selectable={selectable}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onTogglePage={togglePage}
          showSubject={!currentSubject}
          showFolder={folder === "all"}
        />
      ) : (
        <QuestionCards
          questions={questions}
          getHref={(question) => `/questions/${question.uuid}`}
          onOpen={handleStoreFilters}
          onPassage={openPassage}
          footer={(question) => [authorName(question.author), formatDate(question.created, language)].filter(Boolean).join(" · ")}
          selectable={selectable}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          checkbox={draftCheckbox}
        />
      )}
      </div>

      {/* The page on screen next to the pager, as the count line over the list may have given way to the selection. */}
      {questions.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <span className="whitespace-nowrap text-sm text-slate-500">{t("question_list.page_of", { page, pages: totalPages })}</span>
          <Pager page={page} pageCount={totalPages} onPage={pageFromBottom} disabled={loading} label={t("question_list.pagination_bottom")} />
        </div>
      )}
      </>
      )}

      {isAdmin && foldersAvailable && (
        <>
          <FolderNameDialog
            isOpen={folderDialog.open}
            folder={folderDialog.folder}
            onClose={() => setFolderDialog((d) => ({ ...d, open: false }))}
            onSaved={handleFolderSaved}
          />
          <AddSubjectDialog
            isOpen={subjectDialog.open}
            exam={exams.find((e) => e.uuid === subjectDialog.exam?.uuid) || subjectDialog.exam}
            subjects={subjectOptions}
            onClose={() => setSubjectDialog((d) => ({ ...d, open: false }))}
            onAdded={handleSubjectAdded}
          />
          <SubjectSettingsDialog
            isOpen={subjectSettings.open}
            folder={subjectSettings.folder}
            examName={examOf(subjectSettings.folder)?.name || ""}
            onClose={() => setSubjectSettings((d) => ({ ...d, open: false }))}
            onSaved={handleSubjectSaved}
          />
          <DeleteFolderDialog
            isOpen={deleteDialog.open}
            folder={deleteDialog.folder}
            examName={examOf(deleteDialog.folder)?.name || ""}
            onClose={() => setDeleteDialog((d) => ({ ...d, open: false }))}
            onDeleted={handleFolderDeleted}
          />
        </>
      )}
      <ShareFolderDialog
        isOpen={shareDialog.open}
        folder={shareDialog.folder}
        examName={examOf(shareDialog.folder)?.name || ""}
        onClose={() => setShareDialog((d) => ({ ...d, open: false }))}
      />
      {selectable && (
        <MoveToFolderDialog
          isOpen={moveOpen}
          questionIds={[...selectedIds]}
          folders={folders}
          currentFolder={folder}
          allowNoFolder={hasGlobalWrite || isAdmin || folderRoles.includes("moderator")}
          questionCodes={selectedCodesRef.current}
          onDeselect={deselect}
          onClose={() => setMoveOpen(false)}
          onMoved={handleMoved}
        />
      )}
      {currentSubject && (
        <UploadQuestionsDialog
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          folder={currentSubject}
          folderLabel={folderPath(currentSubject, byUuid)}
          allowNoFolder={perms.includes("create_question")}
          subjects={subjectOptions}
          onUploaded={refreshAfterChange}
          onFolderDeleted={() => {
            setFolder("all");
            loadFolders();
          }}
        />
      )}
    </div>
  );
}
